import { Color } from '@/lib/Color';
import { toHex, toOklch } from '@/lib/colorUtils';
import type { HandData } from '@/types/chart';

/**
 * Color scheme interface for assigning colors to hands based on value
 */
export interface ColorScheme {
  name: string;
  description: string;
  getColor(handData: HandData, normalizedValue: number, label?: string): string;
}

/**
 * Color anchor point for interpolated gradients
 */
export interface ColorAnchor {
  position: number; // 0.0 to 1.0
  color: string; // Hex color
}

/**
 * Type-based color scheme - different color families for pairs, suited, and offsuit hands
 * Uses OKLCH interpolation within each hand type's color range for smooth gradients
 */
export class TypeBasedColorScheme implements ColorScheme {
  name = 'Type-Based';
  description = 'Different colors for pairs, suited, and offsuit hands';

  private pairScheme: InterpolatedHeatmapColorScheme;
  private suitedScheme: InterpolatedHeatmapColorScheme;
  private offsuitScheme: InterpolatedHeatmapColorScheme;

  constructor(pairAnchors: ColorAnchor[], suitedAnchors: ColorAnchor[], offsuitAnchors: ColorAnchor[]) {
    this.pairScheme = new InterpolatedHeatmapColorScheme(pairAnchors);
    this.suitedScheme = new InterpolatedHeatmapColorScheme(suitedAnchors);
    this.offsuitScheme = new InterpolatedHeatmapColorScheme(offsuitAnchors);
  }

  getColor(handData: HandData, normalizedValue: number): string {
    switch (handData.type) {
      case 'pair':
        return this.pairScheme.getColor(handData, normalizedValue);
      case 'suited':
        return this.suitedScheme.getColor(handData, normalizedValue);
      case 'offsuit':
        return this.offsuitScheme.getColor(handData, normalizedValue);
    }
  }
}

/**
 * Heatmap color scheme - single color gradient across all hand values
 * Ignores hand type, only considers value
 */
export class HeatmapColorScheme implements ColorScheme {
  name = 'Heatmap';
  description = 'Single color gradient across all values';

  constructor(private colorScale: string[]) {}

  getColor(_handData: HandData, normalizedValue: number): string {
    const index = Math.min(Math.floor(normalizedValue * this.colorScale.length), this.colorScale.length - 1);
    return toHex(this.colorScale[index]);
  }
}

/**
 * Categorical color scheme - assigns distinct colors to enumerated categories
 * Designed for categorical data like positions or actions (2-8 categories)
 */
export class CategoricalColorScheme implements ColorScheme {
  name = 'Categorical';
  description = 'Distinct colors for each category';
  isCategorical = true;

  constructor(private categoryCount: number) {}

  getColor(_handData: HandData, normalizedValue: number): string {
    // Map normalized value back to category index
    // Since normalized values are (index + 1) / count, we reverse it: index = normalized * count - 1
    const categoryIndex = Math.max(
      0,
      Math.min(this.categoryCount - 1, Math.floor(normalizedValue * this.categoryCount - 1)),
    );

    const palette = this.getPalette(this.categoryCount);
    return toHex(palette[categoryIndex]);
  }

  private getPalette(count: number): string[] {
    // Carefully chosen distinct colors for each count
    // Colors progress from cool to warm for visual ordering
    switch (count) {
      case 2:
        return [Color.BLUE_600, Color.RED_600];
      case 3:
        return [Color.BLUE_600, Color.YELLOW_500, Color.RED_600];
      case 4:
        return [Color.BLUE_600, Color.CYAN_500, Color.ORANGE_500, Color.RED_600];
      case 5:
        return [Color.BLUE_700, Color.CYAN_500, Color.GREEN_500, Color.YELLOW_500, Color.RED_600];
      case 6:
        return [Color.BLUE_700, Color.CYAN_600, Color.GREEN_500, Color.YELLOW_500, Color.ORANGE_500, Color.RED_600];
      case 7:
        return [
          Color.INDIGO_700,
          Color.BLUE_600,
          Color.CYAN_500,
          Color.GREEN_500,
          Color.YELLOW_500,
          Color.ORANGE_500,
          Color.RED_600,
        ];
      case 8:
        return [
          Color.INDIGO_700,
          Color.BLUE_600,
          Color.CYAN_500,
          Color.GREEN_500,
          Color.LIME_500,
          Color.YELLOW_500,
          Color.ORANGE_500,
          Color.RED_600,
        ];
      default:
        // Fall back to interpolated gradient for >8 categories
        return this.generateGradient(count);
    }
  }

  private generateGradient(count: number): string[] {
    // For more than 8 categories, generate a gradient
    const colors: string[] = [];
    for (let i = 0; i < count; i++) {
      const t = i / (count - 1);
      // Interpolate between blue and red
      const l = 0.5 + t * 0.2;
      const c = 0.15;
      const h = 250 - t * 280; // Blue (250) to Red (330, wrapped)
      colors.push(`oklch(${l} ${c} ${h})`);
    }
    return colors;
  }
}

/**
 * Interpolated heatmap color scheme - smoothly interpolates between color anchors in OKLCH space
 * Provides smooth gradients without visible banding between colors
 */
export class InterpolatedHeatmapColorScheme implements ColorScheme {
  name = 'Interpolated Heatmap';
  description = 'Smooth color gradient with OKLCH interpolation';

  constructor(private anchors: ColorAnchor[]) {
    // Sort anchors by position to ensure correct interpolation
    this.anchors = [...anchors].sort((a, b) => a.position - b.position);
  }

  getColor(_handData: HandData, normalizedValue: number): string {
    // Clamp value to [0, 1]
    const value = Math.max(0, Math.min(1, normalizedValue));

    // Find the two anchors to interpolate between
    let lowerAnchor = this.anchors[0];
    let upperAnchor = this.anchors[this.anchors.length - 1];

    for (let i = 0; i < this.anchors.length - 1; i++) {
      if (value >= this.anchors[i].position && value <= this.anchors[i + 1].position) {
        lowerAnchor = this.anchors[i];
        upperAnchor = this.anchors[i + 1];
        break;
      }
    }

    // If we're exactly on an anchor, return it directly
    if (value === lowerAnchor.position) {
      return toHex(lowerAnchor.color);
    }
    if (value === upperAnchor.position) {
      return toHex(upperAnchor.color);
    }

    // Calculate interpolation factor between the two anchors
    const t = (value - lowerAnchor.position) / (upperAnchor.position - lowerAnchor.position);

    // Convert both colors to OKLCH
    const lowerOklch = toOklch(lowerAnchor.color);
    const upperOklch = toOklch(upperAnchor.color);

    // Interpolate in OKLCH space
    const l = lowerOklch.l + (upperOklch.l - lowerOklch.l) * t;
    const c = lowerOklch.c + (upperOklch.c - lowerOklch.c) * t;

    // Interpolate hue taking the shortest path around the circle
    let h1 = lowerOklch.h ?? 0;
    let h2 = upperOklch.h ?? 0;

    // If either color is achromatic (no hue), use the other's hue
    if (lowerOklch.c === 0) h1 = h2;
    if (upperOklch.c === 0) h2 = h1;

    // Calculate the shortest path around the hue circle
    let deltaH = h2 - h1;
    if (deltaH > 180) {
      deltaH -= 360;
    } else if (deltaH < -180) {
      deltaH += 360;
    }

    let h = h1 + deltaH * t;
    // Normalize to [0, 360)
    if (h < 0) h += 360;
    if (h >= 360) h -= 360;

    // Convert back to hex
    return toHex({ mode: 'oklch', l, c, h });
  }
}

/**
 * GTO Action type for semantic color mapping
 */
type GtoActionType = 'fold' | 'check' | 'call' | 'raise-small' | 'raise-medium' | 'raise-large' | 'allin' | 'unknown';

/**
 * GTO Action color scheme - assigns colors based on poker action semantics
 * Fold = blue, Call/Check = green, Raise = reds (darker = bigger)
 */
export class GtoActionColorScheme implements ColorScheme {
  name = 'GTO Actions';
  description = 'Semantic colors for poker actions (fold=blue, call=green, raise=red)';

  // Keywords for each action type (case-insensitive matching)
  private static readonly ACTION_PATTERNS: Record<GtoActionType, RegExp[]> = {
    fold: [/\bfold\b/i],
    check: [/\bcheck\b/i, /\bx\b/i],
    call: [/\bcall\b/i, /\blimp\b/i, /\bflat\b/i],
    allin: [/\ball[- ]?in\b/i, /\bjam\b/i, /\bshove\b/i, /\bpush\b/i],
    'raise-small': [/\b(min|small|[12]x|25%|33%)\b/i],
    'raise-medium': [/\b(medium|[34]x|50%|66%|75%)\b/i],
    'raise-large': [/\b(large|big|[5-9]x|\d{2,}x|pot|100%|150%|200%)\b/i],
    unknown: [],
  };

  // Color mapping for each action type
  private static readonly ACTION_COLORS: Record<GtoActionType, string> = {
    fold: Color.BLUE_600,
    check: Color.GREEN_500,
    call: Color.GREEN_600,
    'raise-small': Color.RED_400,
    'raise-medium': Color.RED_600,
    'raise-large': Color.RED_800,
    allin: Color.RED_900,
    unknown: Color.GRAY_500,
  };

  getColor(_handData: HandData, _normalizedValue: number, label?: string): string {
    const actionType = this.detectActionType(label);
    return toHex(GtoActionColorScheme.ACTION_COLORS[actionType]);
  }

  private detectActionType(label?: string): GtoActionType {
    if (!label) return 'unknown';

    const lowerLabel = label.toLowerCase();

    // Check for specific action types in priority order
    // Check allin first since it's specific
    if (this.matchesPatterns(lowerLabel, 'allin')) return 'allin';

    // Check fold
    if (this.matchesPatterns(lowerLabel, 'fold')) return 'fold';

    // Check check
    if (this.matchesPatterns(lowerLabel, 'check')) return 'check';

    // Check call
    if (this.matchesPatterns(lowerLabel, 'call')) return 'call';

    // For raises/bets, check size patterns first, then default to medium
    if (this.isRaiseAction(lowerLabel)) {
      if (this.matchesPatterns(lowerLabel, 'raise-large')) return 'raise-large';
      if (this.matchesPatterns(lowerLabel, 'raise-small')) return 'raise-small';
      if (this.matchesPatterns(lowerLabel, 'raise-medium')) return 'raise-medium';
      // Default raise without size indicator = medium
      return 'raise-medium';
    }

    return 'unknown';
  }

  private isRaiseAction(label: string): boolean {
    return /\b(raise|bet|[234]-?bet|[34]b|open|rfi)\b/i.test(label);
  }

  private matchesPatterns(label: string, actionType: GtoActionType): boolean {
    const patterns = GtoActionColorScheme.ACTION_PATTERNS[actionType];
    return patterns.some((pattern) => pattern.test(label));
  }
}

/**
 * Preset color schemes available for use
 */
export const COLOR_SCHEMES = {
  GTO_ACTIONS: new GtoActionColorScheme(),

  CATEGORICAL_2: new CategoricalColorScheme(2),
  CATEGORICAL_3: new CategoricalColorScheme(3),
  CATEGORICAL_4: new CategoricalColorScheme(4),
  CATEGORICAL_5: new CategoricalColorScheme(5),
  CATEGORICAL_6: new CategoricalColorScheme(6),
  CATEGORICAL_7: new CategoricalColorScheme(7),
  CATEGORICAL_8: new CategoricalColorScheme(8),

  TYPE_BASED_DEFAULT: new TypeBasedColorScheme(
    // Blue for pairs
    [
      { position: 0.0, color: Color.BLUE_700 },
      { position: 0.1, color: Color.BLUE_600 },
      { position: 0.5, color: Color.BLUE_500 },
      { position: 0.9, color: Color.BLUE_400 },
      { position: 1.0, color: Color.BLUE_300 },
    ],
    // Yellow for suited
    [
      { position: 0.0, color: Color.YELLOW_700 },
      { position: 0.1, color: Color.YELLOW_600 },
      { position: 0.5, color: Color.YELLOW_500 },
      { position: 0.9, color: Color.YELLOW_400 },
      { position: 1.0, color: Color.YELLOW_300 },
    ],
    // Red for offsuit
    [
      { position: 0.0, color: Color.RED_700 },
      { position: 0.1, color: Color.RED_600 },
      { position: 0.5, color: Color.RED_500 },
      { position: 0.9, color: Color.RED_400 },
      { position: 1.0, color: Color.RED_300 },
    ],
  ),

  HEATMAP_RED_GREEN: new InterpolatedHeatmapColorScheme([
    { position: 0.0, color: Color.RED_900 },
    { position: 0.2, color: Color.RED_700 },
    { position: 0.35, color: Color.ORANGE_600 },
    { position: 0.5, color: Color.YELLOW_500 },
    { position: 0.65, color: Color.LIME_500 },
    { position: 0.8, color: Color.GREEN_500 },
    { position: 1.0, color: Color.GREEN_400 },
  ]),

  HEATMAP_BLUE_RED: new InterpolatedHeatmapColorScheme([
    { position: 0.0, color: Color.BLUE_900 },
    { position: 0.2, color: Color.BLUE_700 },
    { position: 0.35, color: Color.CYAN_600 },
    { position: 0.5, color: Color.YELLOW_500 },
    { position: 0.7, color: Color.ORANGE_500 },
    { position: 0.85, color: Color.RED_500 },
    { position: 1.0, color: Color.RED_400 },
  ]),

  HEATMAP_VIRIDIS: new InterpolatedHeatmapColorScheme([
    { position: 0.0, color: Color.PURPLE_900 },
    { position: 0.2, color: Color.INDIGO_700 },
    { position: 0.35, color: Color.BLUE_600 },
    { position: 0.5, color: Color.CYAN_500 },
    { position: 0.65, color: Color.GREEN_500 },
    { position: 0.8, color: Color.LIME_400 },
    { position: 1.0, color: Color.YELLOW_300 },
  ]),

  HEATMAP_PASTEL: new InterpolatedHeatmapColorScheme([
    { position: 0.0, color: Color.PINK_300 },
    { position: 0.2, color: Color.ORANGE_200 },
    { position: 0.35, color: Color.YELLOW_200 },
    { position: 0.5, color: Color.LIME_200 },
    { position: 0.65, color: Color.CYAN_200 },
    { position: 0.8, color: Color.SKY_200 },
    { position: 1.0, color: Color.VIOLET_300 },
  ]),
} as const;

/**
 * Valid color scheme keys
 */
export type ColorSchemeKey = keyof typeof COLOR_SCHEMES;
